import { ClientConfig } from 'client/configuration';
import { featureFlags } from 'client/utils/feature-flags';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { createInstance, Identify } from '@amplitude/analytics-browser';
import { isE2ETest } from 'site-modules/financing/utils/financing-utils';
import { requestContext } from 'client/utils/request-context/request-context';
import { logger } from 'client/utils/logger/logger';
import { generateId } from './idGenerator';

function getPageProps() {
  return {
    request_url: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
    referrer: document.referrer || null,
    referrer_domain: document.referrer ? new URL(document.referrer).hostname : null,
  };
}

const amplitudeClient = createInstance();

export async function initAmplitude(state) {
  const isAmplitudeEnabled = featureFlags.get('amplitude');
  const clientIsBot = requestContext.get('loggerMetadata')?.isBot;

  if (!isAmplitudeEnabled || isE2ETest(location) || clientIsBot) {
    return;
  }

  const { id: edwVisitorId, session: edwSessionId, isNewSession } = state.visitor;

  const { xApiKey } = ClientConfig.get('amplitude');

  const hashedUserId = generateId(edwVisitorId).toString();
  const deviceId = hashedUserId;
  const amplitudeSessionId = isNewSession ? Date.now() : undefined;

  try {
    const replayPlugin = sessionReplayPlugin();

    await amplitudeClient.add(replayPlugin).promise;

    await amplitudeClient.init(xApiKey, hashedUserId, {
      deviceId,
      sessionId: amplitudeSessionId,
      autocapture: {
        attribution: false,
        pageViews: false,
        sessions: true,
        formInteractions: false,
        fileDownloads: false,
        elementInteractions: false,
        pageUrlEnrichment: false,
        webVitals: false,
      },
    }).promise;

    const visitorSessionKey = `${hashedUserId}*${edwSessionId}`;

    amplitudeClient.setGroup('visitor_session_key', visitorSessionKey);

    const identifyEvent = new Identify();
    identifyEvent.set('visitor_session_key', visitorSessionKey);

    await amplitudeClient.identify(identifyEvent).promise;

    if (isNewSession) {
      const result = await amplitudeClient.track('session_start', getPageProps()).promise;

      if (result.code !== 200) {
        logger.warn(
          {
            code: result.code,
            message: result.message,
            details: result.body?.error,
          },
          'Failed to send session_start event.'
        );
      }
    }
  } catch (error) {
    logger.warn(error, 'Failed to initialize Amplitude or Session Replay');
  }
}
